import React from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { tiptapDocToPlainText, type TiptapDoc } from '../rich-editor';

export interface ChartNoteCardProps {
    title: string;
    doc: TiptapDoc;
    onEdit?: () => void;
    onDelete?: () => void;
}

/** Read-only chart-note card, styled like a patient note. Renders a
 *  line-clamped plain-text preview; action buttons appear only when handlers are given. */
export const ChartNoteCard: React.FC<ChartNoteCardProps> = ({ title, doc, onEdit, onDelete }) => {
    const plainText = tiptapDocToPlainText(doc).trim();

    return (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3.5">
            <div className="flex items-start justify-between gap-3 mb-1.5">
                <span className="flex-1 min-w-0 text-[13px] font-bold text-gray-800 font-inter break-words">
                    {title.trim() || 'Untitled chart note'}
                </span>
                {(onEdit || onDelete) && (
                    <div className="flex items-center gap-1 shrink-0">
                        {onEdit && (
                            <button
                                type="button"
                                onClick={onEdit}
                                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                                aria-label="Edit chart note"
                            >
                                <PencilIcon className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {onDelete && (
                            <button
                                type="button"
                                onClick={onDelete}
                                className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-red-600 transition-colors"
                                aria-label="Delete chart note"
                            >
                                <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                )}
            </div>
            <p className="text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap line-clamp-4">
                {plainText}
            </p>
        </div>
    );
};

export default ChartNoteCard;
